import type { ReactNode } from 'react';

// Components shared by the marketing site, the customer dashboard and admin.

export type ShellConfig = { hosts: { public: string } };

export function Logo() {
  return <span className="logo" aria-hidden="true"><svg viewBox="0 0 64 64"><path d="M8 15h43a5 5 0 0 1 5 5v27a5 5 0 0 1-5 5H13a5 5 0 0 1-5-5V20a5 5 0 0 1 5-5Zm2 6 20 15a4 4 0 0 0 5 0l19-15v-1H10Z"/><path className="bolt" d="m43 28-10 17h8l-3 14 14-20h-9l6-11Z"/></svg></span>;
}

const navIcons: Record<string, string> = {
  overview: 'M4 13h6V4H4v9Zm10 7h6V11h-6v9ZM4 20h6v-3H4v3Zm10-13h6V4h-6v3Z',
  domains: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.2-2.5 3.4-5.5 3.4-9S14.2 5.5 12 3m0 18c-2.2-2.5-3.4-5.5-3.4-9S9.8 5.5 12 3M3.5 9h17m-17 6h17',
  inbox: 'M4 5h16v14H4V5Zm0 9h4l2 2h4l2-2h4',
  developer: 'm8 8-4 4 4 4m8-8 4 4-4 4m-2-11-4 14',
  billing: 'M3 6h18v13H3V6Zm0 4h18M7 15h4',
  team: 'M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.9M16 2.1a4 4 0 0 1 0 7.8',
  health: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z',
  customers: 'M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m7.5-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm12.5 10v-2a4 4 0 0 0-3-3.9M17 3.1a4 4 0 0 1 0 7.8',
  mail: 'M3 5h18v14H3V5Zm0 1 9 7 9-7',
  relays: 'M12 20V10m-4 10h8M9 10l3-7 3 7M6.5 7.5a8 8 0 0 0 0 8m11-8a8 8 0 0 1 0 8',
  'free service': 'm12 3 1.4 4.1L17.5 8.5l-4.1 1.4L12 14l-1.4-4.1-4.1-1.4 4.1-1.4L12 3Zm6 11 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14ZM6 14l1 2.7L10 18l-3 1.3L6 22l-1-2.7L2 18l3-1.3L6 14Z',
  abuse: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Zm0-14v5m0 4h.01',
  audit: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
};

function NavIcon({ name }: { name: string }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={navIcons[name] || 'M5 12h14'} /></svg>;
}

export function DashboardShell({ config, title, tab, setTab, tabs, children, onLogout, admin = false, status }: {
  config: ShellConfig; title: string; tab: string; setTab: (value: string) => void; tabs: string[]; children: ReactNode;
  onLogout?: () => void; admin?: boolean; status?: ReactNode;
}) {
  return <div className="dashboard">
    <aside>
      <a className="brand" href={`https://${config.hosts.public}`}><Logo /><span>MailBots</span></a>
      <div className="workspace"><small>{admin ? 'ADMIN' : 'WORKSPACE'}</small><b>{title}</b></div>
      <nav>{tabs.map((item) => <button className={tab === item ? 'active' : ''} aria-label={item} title={item} aria-current={tab === item ? 'page' : undefined} onClick={() => setTab(item)} key={item}><span className="nav-icon"><NavIcon name={item} /></span><span className="nav-label">{item}</span></button>)}</nav>
      <div className="aside-foot">{status ?? <><span className="status-dot" /> Systems nominal</>}{onLogout && <button onClick={onLogout}>Sign out</button>}</div>
    </aside>
    <main>
      <div className="dash-head"><div><small>{admin ? 'OPERATIONS' : 'MAILBOTS CONTROL'}</small><h1>{title}</h1></div><a className="button small secondary" href={`https://${config.hosts.public}/guide`}>Docs ↗</a></div>
      {children}
    </main>
  </div>;
}

export function FullLoader() { return <div className="full-loader"><Logo /><span /></div>; }
export function Banner({ children, tone }: { children: ReactNode; tone?: 'info' }) { return <div className={tone ? `banner ${tone}` : 'banner'}>{children}</div>; }
export function Metric({ label, value, note }: { label: string; value: string; note: string }) { return <article className="metric"><small>{label}</small><strong>{value}</strong><span>{note}</span></article>; }
export function Status({ value }: { value: string }) { return <span className={`status ${value}`}>{value.replace(/_/g, ' ')}</span>; }
export function Empty({ text }: { text: string }) { return <div className="empty"><span>∅</span><p>{text}</p></div>; }
